import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { fetchBotWinRateStats, fetchBotExitSignals } from '@/services/conditional-skip-stats-service';

interface BotWinRateDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  botId: string;
  botName?: string;
}

export const BotWinRateDetailDialog: React.FC<BotWinRateDetailDialogProps> = ({
  open,
  onOpenChange,
  botId,
  botName
}) => {
  const [page, setPage] = useState(1);
  const [winFilter, setWinFilter] = useState<'all' | 'win' | 'loss'>('all');
  const [forwardTypeFilter, setForwardTypeFilter] = useState<'all' | 'real' | 'virtual'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 20;

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['bot-win-rate-stats', botId],
    queryFn: () => fetchBotWinRateStats(botId),
    enabled: open && !!botId
  });

  // Fetch signals
  const { data: signalsData, isLoading: signalsLoading } = useQuery({
    queryKey: ['bot-exit-signals', botId, page, winFilter, forwardTypeFilter, searchQuery],
    queryFn: () => fetchBotExitSignals(botId, page, pageSize, winFilter, searchQuery, forwardTypeFilter),
    enabled: open && !!botId
  });

  const signals = signalsData?.signals || [];
  const total = signalsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[66vw] w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{botName || 'Bot'}</DialogTitle>
          <DialogDescription>
            Win Rate Analytics - Based on Forwarded EXIT Signals
          </DialogDescription>
        </DialogHeader>

        {statsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground">
                    Total Exits Forwarded
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.total_forwarded_exits || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    response_status = 200
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground">Wins</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-500">{stats?.wins || 0}</div>
                  <p className="text-xs text-muted-foreground">win = 1</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground">Losses</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-500">{stats?.losses || 0}</div>
                  <p className="text-xs text-muted-foreground">win = 0</p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground">Win Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={cn(
                    "text-3xl font-bold",
                    (stats?.win_rate || 0) >= 60 ? "text-green-500" : 
                    (stats?.win_rate || 0) >= 50 ? "text-orange-400" : 
                    "text-red-500"
                  )}>
                    {stats?.win_rate?.toFixed(1) || '0.0'}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {stats?.wins || 0}W / {stats?.losses || 0}L
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* EXIT Signals History Table */}
            <Card>
              <CardHeader>
                  <div className="flex items-center justify-between">
                  <CardTitle>Forwarded EXIT Signals History</CardTitle>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Search instrument..." 
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                      }}
                      className="w-48"
                    />
                    <Select value={winFilter} onValueChange={(value: any) => {
                      setWinFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="win">Win Only</SelectItem>
                        <SelectItem value="loss">Loss Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={forwardTypeFilter} onValueChange={(value: any) => {
                      setForwardTypeFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="All Forwards" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Forwards</SelectItem>
                        <SelectItem value="real">Real Only</SelectItem>
                        <SelectItem value="virtual">Virtual Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {signalsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : signals.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No signals found
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Instrument</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead>Response</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {signals.map((signal) => (
                          <TableRow key={signal.id}>
                            <TableCell className="text-xs">
                              {format(new Date(signal.signal_timestamp), 'MMM dd HH:mm:ss')}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={signal.action === 'EXIT_LONG' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {signal.action}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {signal.instrument}
                            </TableCell>
                            <TableCell>
                              {signal.win === 1 ? (
                                <Badge className="bg-green-500 text-white">Win</Badge>
                              ) : (
                                <Badge variant="destructive">Loss</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="flex items-center gap-1">
                                <Badge variant="outline">{signal.response_status}</Badge>
                                {signal.is_virtual_forward && (
                                  <Badge variant="outline" className="text-orange-500 border-orange-500">
                                    🔄 Virtual
                                  </Badge>
                                )}
                                {signal.response_ms && (
                                  <span className="text-muted-foreground">
                                    {signal.response_ms}ms
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">
                        Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} signals
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={page === 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Previous
                        </Button>
                        <div className="flex items-center px-3 text-sm">
                          Page {page} of {totalPages}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={page >= totalPages}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
